import { ColorNordicGrey2, Spacing16, Spacing24, Spacing32, Spacing8 } from 'autogen/design-tokens/tokens';
import { StripedVerticalSpacer } from 'components';
import ContainerFluid from 'components/Layout/components/ContainerFluid';
import Tag from 'components/generic/tag/Tag';
import { Typography } from 'components/generic/typography';
import { getTheme } from 'helpers/designThemes';
import dynamic from 'helpers/dynamic';
import useSegmentation from 'hooks/user/useSegmentation';
import React, { type HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import type { ColorTheme } from 'types/tokens';
import { breakpoint, sizes } from 'variables';

type SupportedThemes = Extract<ColorTheme, 'ClubMatas'>;

export type ContentHeaderProps = {
    tag: string;
    title: string;
    theme: SupportedThemes;
};

const Wrapper = styled.header``;

const Container = styled.div`
    padding: ${Spacing24} ${Spacing16};
    background-color: ${ColorNordicGrey2};

    ${breakpoint.up(sizes.md)} {
        padding: ${Spacing32} ${Spacing16};
    }
`;

const Grid = styled(ContainerFluid)`
    display: grid;
    gap: ${Spacing8};
    grid-template-areas:
        'tag logo'
        'title title';

    ${breakpoint.up(sizes.md)} {
        grid-template-areas:
            'tag logo'
            'title logo';
    }
`;

const StyledTag = styled(Tag)`
    grid-area: tag;
    justify-self: flex-start;
`;

const Headline = styled(Typography.Headline)`
    grid-area: title;
`;

const logoCss = css`
    grid-area: logo;
    justify-self: flex-end;
    align-self: flex-start;

    ${breakpoint.up(sizes.md)} {
        align-self: center;
    }
`;

const ClubMatasLogo = styled(dynamic(() => import('icons/logos/matas/ClubMatasHorizontal.svg?react')))`
    width: 90px;
    height: auto;

    ${breakpoint.up(sizes.md)} {
        width: 110px;
    }

    ${logoCss}
`;

function getLogo(theme: ContentHeaderProps['theme']) {
    switch (theme) {
        case 'ClubMatas':
        default:
            return ClubMatasLogo;
    }
}

export function ContentHeaderContainer({ children, ...restProps }) {
    const { tenant } = useSegmentation();
    const isMatas = tenant === 'Matas';

    return (
        <Wrapper {...restProps}>
            <Container>{children}</Container>
            {isMatas && <StripedVerticalSpacer themeName="NordicGrey" transparentSecondColor />}
        </Wrapper>
    );
}

export default function ContentHeader(props: ContentHeaderProps & HTMLAttributes<HTMLDivElement>) {
    const { tag, title, theme, ...restProps } = props;
    const { color3 } = getTheme(theme);
    const Logo = getLogo(theme);

    return (
        <ContentHeaderContainer {...restProps}>
            <Grid>
                <StyledTag backgroundColor={color3}>{tag}</StyledTag>
                <Headline size="M">{title}</Headline>
                <Logo />
            </Grid>
        </ContentHeaderContainer>
    );
}
